import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Ota } from 'frontend-utils/models';
import HotelRatesTablesCellsThHotel from 'lumen/components/hotel/rates/tables/cells/th-hotel';
import ParityTablesCellsTdDate from 'lumen/components/parity/tables/cells/td-date';
import ParityTablesCellsTdPosBaserate from 'lumen/components/parity/tables/cells/td-pos-baserate';
import ParityTablesCellsTdPosCountry from 'lumen/components/parity/tables/cells/td-pos-country';
import ParityTablesCellsTdPosLinkout from 'lumen/components/parity/tables/cells/td-pos-linkout';
import ParityTablesCellsTdPosLowestRateOverOtas from 'lumen/components/parity/tables/cells/td-pos-lowest-rate-over-otas';
import ParityTablesCellsTdPosOtasWithIssues from 'lumen/components/parity/tables/cells/td-pos-otas-with-issues';
import type { Period } from 'lumen/models';

const COLUMNS = {
  DATE: 'date',
  POS: 'pos',
  RATE: 'rate',
  LOWEST_RATE_MAJOR_OTAS: 'lowest-rate-major-otas',
  MAJOR_OTAS: 'major-otas',
  LOWEST_RATE_OTHER_OTAS: 'lowest-rate-other-otas',
  OTHER_OTAS: 'other-otas',
  LIVE_VIEW: 'live-view',
};

interface ParityTablesTableParityPosSignature {
  Args: {
    availableOTAs: Ota[];
    isLoading: boolean;
    model: Period[];
  };
}

export default class ParityTablesTableParityPos extends Component<ParityTablesTableParityPosSignature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('general.date'),
        valuePath: COLUMNS.DATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdDate,
        textAlign: 'left',
        minWidth: 120,
        maxWidth: 120,
        hasDayLiveShop: true,
        parityType: 'parity_pos',
        firstPos: this.args.model[0]?.pos,
      },
      {
        name: this.intl.t('parity.pos.pos'),
        valuePath: COLUMNS.POS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosCountry,
        textAlign: 'center',
        minWidth: 50,
        maxWidth: 50,
        hasMargin: false,
      },
      {
        name: this.intl.t('parity.pos.hotel_rate'),
        valuePath: COLUMNS.RATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosBaserate,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasRightBorder: true,
        infoText: this.intl.t('parity.pos.hotel_rate_info'),
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('general.major_otas'),
        valuePath: COLUMNS.MAJOR_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosOtasWithIssues,
        minWidth: 220,
        maxWidth: 300,
        textAlign: 'left',
        searchMajorOtas: true,
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('rates.lowest_rate'),
        valuePath: COLUMNS.LOWEST_RATE_MAJOR_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLowestRateOverOtas,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasMargin: false,
        hasRightBorder: true,
        searchMajorOtas: true,
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('general.other_otas'),
        valuePath: COLUMNS.OTHER_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosOtasWithIssues,
        textAlign: 'left',
        minWidth: 220,
        maxWidth: 300,
        searchMajorOtas: false,
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('rates.lowest_rate'),
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLowestRateOverOtas,
        valuePath: COLUMNS.LOWEST_RATE_OTHER_OTAS,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasMargin: false,
        hasRightBorder: true,
        searchMajorOtas: false,
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('parity.pos.live'),
        valuePath: COLUMNS.LIVE_VIEW,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLinkout,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasRightBorder: true,
        infoTextTitle: this.intl.t('parity.live_view').toString(),
        infoText: this.intl.t('parity.live_view_tooltip').toString(),
      },
    ];

    return columns;
  }

  get rows(): CustomEmberTableRow[] {
    const rows: CustomEmberTableRow[] = [];
    this.args.model.forEach((period) => {
      const row = {
        [COLUMNS.DATE]: period,
        [COLUMNS.POS]: period.pos,
        [COLUMNS.RATE]: period,
        [COLUMNS.LOWEST_RATE_MAJOR_OTAS]: period,
        [COLUMNS.MAJOR_OTAS]: period,
        [COLUMNS.LOWEST_RATE_OTHER_OTAS]: period,
        [COLUMNS.OTHER_OTAS]: period,
        [COLUMNS.LIVE_VIEW]: period,
      };
      rows.push(row);
    });

    return rows;
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @isLoading={{@isLoading}}
      @rows={{this.rows}}
      @widthConstraint="gte-container-slack"
    />
  </template>
}
